import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLogout, type Me } from "../auth/session";

const ROLE_NAME: Record<Me["role"], string> = {
  office: "Office",
  provider: "Field operators",
  supplier: "Supplier",
};

/** "office@prv.local" -> "OF"; "thabo.mokoena@..." -> "TM". */
export function initials(email: string): string {
  const local = email.split("@")[0] ?? "";
  const words = local.split(/[._-]+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase() || "?";
}

/** The avatar at the right of the masthead, and what hangs off it: who you
 * are, the screens that are somewhere you go rather than a step in the work
 * (Admin, Help -- whatever `/me` places in the account menu), and the way
 * out. */
export default function AccountMenu({ me }: { me: Me }) {
  const logout = useLogout();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const entries = me.screens.filter((screen) => screen.place === "account");

  useEffect(() => {
    if (!open) {
      return;
    }
    function away(event: MouseEvent) {
      if (box.current !== null && !box.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function escape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div className="account" ref={box}>
      <button
        type="button"
        className="avatar"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${me.email}`}
        onClick={() => setOpen((was) => !was)}
      >
        {initials(me.email)}
      </button>
      {open ? (
        <div className="account-menu" role="menu">
          <div className="account-who">
            <strong>{me.email}</strong>
            <span>{ROLE_NAME[me.role]} · Skakuza Trading</span>
          </div>
          {entries.map((screen) => (
            <Link key={screen.key} role="menuitem" to={screen.href} onClick={() => setOpen(false)}>
              {screen.label}
            </Link>
          ))}
          <button
            type="button"
            role="menuitem"
            className="account-out"
            disabled={logout.isPending}
            onClick={() =>
              logout.mutate(undefined, {
                onSuccess: () => {
                  setOpen(false);
                  navigate("/login");
                },
              })
            }
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
